import { useEffect, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import {
	Button,
	Col,
	Form,
	Input,
	Row,
	Select,
	Skeleton,
	Spin,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { actions as routerActions } from "redux-router5";
import type { Dispatch } from "@reduxjs/toolkit";
import type { State as RouterState } from "router5";
import type { IntlShape } from "@formatjs/intl";
import ConnectedAppLayout from "../AppLayout";
import { PageTitleContainer } from "../../common/components/PageTitle";
import { Card } from "../../common/components/Card";
import ModalFooter from "../../common/components/ModalFooter";
import { routeNames } from "../common/routes";
import type { RootState } from "../store";
import type {
	CurrentUser,
	UserGroup,
	UserProfile,
} from "../generated-axios-client";
import { userActions } from "../../features/user/userSlice";
import { isAdministrator } from "../../common/utils/isAdministrator";

type Props = {
	dispatch: Dispatch;
	route: RouterState | null;
	currentUser: CurrentUser | undefined;
	intl: IntlShape;
};

export function EditUserProfilePage({
	dispatch,
	route,
	currentUser,
	intl,
}: Props) {
	const [form] = Form.useForm();
	const submitting = useSelector((state: RootState) => state.userstate.updateProfileLoading);

	const profileId = route?.params?.id as string | undefined;
	const isAdmin = isAdministrator(currentUser?.groups);

	const profile = useSelector((state: RootState) => state.userstate.viewedProfile) as UserProfile | null;
	const usergroups = useSelector((state: RootState) => state.userstate.groups) as UserGroup[];
	const loading = profile === null;

	useEffect(() => {
		if (!profile) return;
		form.setFieldsValue({
			first_name: profile.first_name ?? "",
			last_name: profile.last_name ?? "",
			phone_number: profile.phone_number ?? "",
			group: profile.groups?.[0] ?? null,
			technician_level: profile.technician_level ?? null,
		});
	}, [profile, form]);

	useEffect(() => {
		if (!loading && profile) {
			const isOwnProfile = currentUser?.id === profile.id;
			if (!isOwnProfile && !isAdmin) {
				dispatch(
					routerActions.navigateTo(routeNames.USER_PROFILE_VIEW, {
						id: profileId,
					}),
				);
			}
		}
	}, [loading, profile, currentUser, isAdmin, dispatch, profileId]);

	const selectedGroupId = Form.useWatch("group", form);
	const selectedGroup = useMemo(
		() => usergroups.find((g) => g.id === selectedGroupId),
		[usergroups, selectedGroupId],
	);
	const isTechnician = selectedGroup?.name?.toLowerCase() === "technician";

	const save = () => {
		form
			.validateFields()
			.then((values) => {
				const { group, technician_level, ...rest } = values;
				const payload: Partial<UserProfile> = {
					first_name: rest.first_name,
					last_name: rest.last_name,
					phone_number: rest.phone_number || null,
				};
				if (isAdmin) {
					payload.groups = group != null ? [group] : [];
					if (isTechnician) {
						payload.technician_level = technician_level ?? null;
					} else {
						payload.technician_level = null;
					}
				}
				dispatch(userActions.updateProfile({ profileId: profileId!, payload }));
			})
			.catch(() => {});
	};

	if (!route) return null;

	if (loading) {
		return (
			<ConnectedAppLayout>
				<PageTitleContainer
					title={intl.formatMessage({ id: "profile_edit_title" })}
				>
					<Skeleton active />
				</PageTitleContainer>
			</ConnectedAppLayout>
		);
	}

	if (!profile) {
		return (
			<ConnectedAppLayout>
				<PageTitleContainer
					title={intl.formatMessage({ id: "profile_edit_title" })}
				>
					<Spin indicator={<LoadingOutlined spin />} size="large" />
				</PageTitleContainer>
			</ConnectedAppLayout>
		);
	}

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "profile_edit_title" })}
			>
				<Form form={form} layout="vertical">
					<Card title={intl.formatMessage({ id: "profile_section_info" })}>
						<Row gutter={24}>
							<Col xs={24} md={12}>
								<Form.Item
									name="first_name"
									label={intl.formatMessage({ id: "profile_first_name" })}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item
									name="last_name"
									label={intl.formatMessage({ id: "profile_last_name" })}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={24}>
							<Col xs={24} md={12}>
								<Form.Item
									label={intl.formatMessage({ id: "profile_username" })}
									extra={intl.formatMessage({
										id: "profile_username_readonly_hint",
									})}
								>
									<Input value={profile.username} disabled />
								</Form.Item>
							</Col>
							<Col xs={24} md={12}>
								<Form.Item
									name="phone_number"
									label={intl.formatMessage({ id: "profile_phone" })}
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={24}>
							<Col xs={24} md={12}>
								<Form.Item
									label={intl.formatMessage({ id: "profile_email" })}
									extra={intl.formatMessage({
										id: "profile_email_readonly_hint",
									})}
								>
									<Input value={profile.email} disabled />
								</Form.Item>
							</Col>
							{isAdmin && (
								<Col xs={24} md={12}>
									<Form.Item
										name="group"
										label={intl.formatMessage({ id: "user_group" })}
									>
										<Select
											allowClear
											options={usergroups.map((g) => ({
												label: g.name,
												value: g.id,
											}))}
										/>
									</Form.Item>
								</Col>
							)}
						</Row>
						{isAdmin && isTechnician && (
							<Row gutter={24}>
								<Col xs={24} md={12}>
									<Form.Item
										name="technician_level"
										label={intl.formatMessage({ id: "user_technician_level" })}
									>
										<Select
											allowClear
											options={[
												{
													label: intl.formatMessage({
														id: "user_technician_level_l1",
													}),
													value: "L1",
												},
												{
													label: intl.formatMessage({
														id: "user_technician_level_l2",
													}),
													value: "L2",
												},
												{
													label: intl.formatMessage({
														id: "user_technician_level_l3",
													}),
													value: "L3",
												},
											]}
										/>
									</Form.Item>
								</Col>
							</Row>
						)}
					</Card>
				</Form>
				<ModalFooter>
					<Button
						onClick={() =>
							dispatch(
								routerActions.navigateTo(routeNames.USER_PROFILE_VIEW, {
									id: profileId,
								}),
							)
						}
					>
						{intl.formatMessage({ id: "ticket_button_title_cancel" })}
					</Button>
					<Button type="primary" loading={submitting} onClick={save}>
						{intl.formatMessage({ id: "profile_edit_save" })}
					</Button>
				</ModalFooter>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
}

export const ConnectedEditUserProfilePage = connect((state: RootState) => ({
	route: state.router.route,
	currentUser: state.userstate.user,
	intl: state.intl.datetimeIntl,
}))(EditUserProfilePage);

export default ConnectedEditUserProfilePage;
